import useSWR from "swr"

import fetchCommerceToolsGraphQLApi from "@secureo/common/utils/commerceTools/graphql/fetchCommerceToolsGraphQLApi"
import { getFilteredPricesByCountryCodeAndCurrency } from "@secureo/common/utils/commerceTools/formatters/formatProduct"
import getRawAttributeByName from "@secureo/common/utils/commerceTools/getRawAttributeByName"
import getShippingOriginationCountryCodesByShopCountry from "@secureo/common/utils/commerceTools/formatters/attributes/getShippingOriginationCountryCodesByShopCountry"

import {
	PricesByCountryCode,
	ShippingOriginationCountryCodesByDestinationCountryCode,
	RawShippingOriginationCountryCodeAttribute,
} from "@secureo/common/typings/Product"
import { Product } from "@secureo/common/typings/generated/commercetools-graphql"
import { CurrencyCode } from "@secureo/common/typings/Forex"

export interface UpSellSellProduct {
	productId: string
	variantId: number
	name: string
	sku: string
	slug: string
	image: string
	manufacturer: string
	shortDescription: string
	pricesByCountryCode: PricesByCountryCode
	shippingOriginationCountryCodesByDestinationCountryCode: ShippingOriginationCountryCodesByDestinationCountryCode
}

const query = /* GraphQL */ `
	query upSellSellProducts($skus: [String!]!) {
		products(skus: $skus) {
			results {
				id
				masterData {
					current {
						slug(locale: "de")
						name(locale: "de")
						masterVariant {
							sku
							prices {
								channel {
									key
								}
								country
								value {
									centAmount
									currencyCode
								}
							}
							images {
								url
							}
							attributesRaw(
								includeNames: [
									"shippingOriginationCountryCode"
									"manufacturer"
									"shortDescriptionDigishop"
								]
							) {
								name
								value
							}
						}
					}
				}
			}
		}
	}
`

const formatUpSellProduct = (rawProduct: Product, currencyCode: CurrencyCode) => {
	const {
		id: productId,
		masterData: {
			current: {
				name,
				slug,
				masterVariant: { sku, prices = [], images = [], attributesRaw = [] },
			},
		},
	} = rawProduct

	const pricesByCountryCode = getFilteredPricesByCountryCodeAndCurrency(prices, currencyCode)

	const image = images?.[0]?.url ?? null

	const shortDescription =
		getRawAttributeByName(attributesRaw, "shortDescriptionDigishop")?.value ?? ""
	const manufacturer =
		getRawAttributeByName(attributesRaw, "manufacturer")?.value?.label?.de ?? ""
	const shippingOriginationCountryCodeAttribute = getRawAttributeByName(
		attributesRaw,
		"shippingOriginationCountryCode",
	) as RawShippingOriginationCountryCodeAttribute
	const shippingOriginationCountryCodesByDestinationCountryCode =
		getShippingOriginationCountryCodesByShopCountry(shippingOriginationCountryCodeAttribute)

	const formattedUpSellProduct: UpSellSellProduct = {
		productId,
		variantId: 1,
		sku,
		name,
		slug,
		image,
		pricesByCountryCode,
		shortDescription,
		manufacturer,
		shippingOriginationCountryCodesByDestinationCountryCode,
	}

	return formattedUpSellProduct
}

const fetchUpSellProducts = async (
	skus: string[],
	accessToken: string,
	currencyCode: CurrencyCode,
) => {
	const data = (await fetchCommerceToolsGraphQLApi(accessToken, query, {
		skus,
	})) as {
		products: {
			results: Product[]
		}
	}

	const upSellProducts = data?.products?.results ?? []
	const formattedUpSellProducts = upSellProducts.map((upSellProduct) =>
		formatUpSellProduct(upSellProduct, currencyCode),
	)

	return formattedUpSellProducts
}

const useUpSellProducts = (
	accessToken: string,
	upSellProductSkus: string[],
	currencyCode: CurrencyCode,
) => {
	const {
		data: upSellProducts,
		error,
		isValidating,
	} = useSWR(
		accessToken ? [upSellProductSkus, accessToken, currencyCode] : null,
		([upSellProductSkus, accessToken, currencyCode]) =>
			fetchUpSellProducts(upSellProductSkus, accessToken, currencyCode),
		{
			fallbackData: [],
			revalidateOnMount: true,
		},
	)

	return { upSellProducts, isValidating, error }
}

export default useUpSellProducts
